import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const TooltipModeContext = React.createContext({ isTouchDevice: false })

const useIsTouchTooltipMode = () => {
  const [isTouchDevice, setIsTouchDevice] = React.useState(false)

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined
    }

    const mediaQuery = window.matchMedia("(hover: none), (pointer: coarse)")
    const updateMode = () => setIsTouchDevice(mediaQuery.matches)

    updateMode()
    mediaQuery.addEventListener?.("change", updateMode)

    return () => mediaQuery.removeEventListener?.("change", updateMode)
  }, [])

  return isTouchDevice
}

const Tooltip = ({ children, open: openProp, defaultOpen = false, onOpenChange, ...props }) => {
  const isTouchDevice = useIsTouchTooltipMode()
  const [mobileOpen, setMobileOpen] = React.useState(defaultOpen)
  const isControlled = typeof openProp === "boolean"
  const open = isControlled ? openProp : mobileOpen
  const handleOpenChange = onOpenChange || setMobileOpen

  if (isTouchDevice) {
    return (
      <TooltipModeContext.Provider value={{ isTouchDevice: true }}>
        <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange} modal={false}>
          {children}
        </PopoverPrimitive.Root>
      </TooltipModeContext.Provider>
    )
  }

  return (
    <TooltipModeContext.Provider value={{ isTouchDevice: false }}>
      <TooltipPrimitive.Root
        open={openProp}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        {...props}
      >
        {children}
      </TooltipPrimitive.Root>
    </TooltipModeContext.Provider>
  )
}

const TooltipTrigger = React.forwardRef(({ ...props }, ref) => {
  const { isTouchDevice } = React.useContext(TooltipModeContext)

  if (isTouchDevice) {
    return <PopoverPrimitive.Trigger ref={ref} {...props} />
  }

  return <TooltipPrimitive.Trigger ref={ref} {...props} />
})
TooltipTrigger.displayName = "TooltipTrigger"

const TooltipContent = React.forwardRef(({ className, sideOffset = 4, ...props }, ref) => {
  const { isTouchDevice } = React.useContext(TooltipModeContext)

  if (isTouchDevice) {
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          sideOffset={8}
          collisionPadding={16}
          align="center"
          onOpenAutoFocus={(event) => event.preventDefault()}
          className={cn(
            "hover-contrast-tooltip z-50 w-[min(20rem,calc(100vw-2rem))] overflow-visible whitespace-normal break-words rounded-2xl px-4 py-3 text-sm leading-6 shadow-[0_18px_45px_rgba(28,25,23,0.18)] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 origin-[--radix-popover-content-transform-origin]",
            className
          )}
          {...props}
        />
      </PopoverPrimitive.Portal>
    )
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "hover-contrast-tooltip z-50 overflow-hidden px-3 py-1.5 text-xs animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
          className
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
})
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
